import { useAuth } from "@clerk/clerk-react";
import {
  Box,
  Button,
  DialogContent,
  Divider,
  Grid,
  Link,
  Typography,
  useMediaQuery,
  useTheme,
} from "@mui/material";
import { AlertContext } from "contexts/Alert";
import { OnboardingDialogsContext, Page } from "contexts/OnboardingDialogs";
import { OrganizationUserContext } from "contexts/Organization";
import { OutreachTemplatesContext } from "contexts/OutreachTemplates";
import { useContext, useState } from "react";
import { useLocation } from "react-router-dom";
import { Link as RouterLink } from "react-router-dom";
import { Map } from "schemas/functions";
import { routes } from "schemas/routes";

import {
  Template,
  TemplateContentType,
  TemplateMethod,
} from "features/Influencer/ContactList/schema";
import ConversationalTemplate from "features/Influencer/HomePage/CreateTemplateDialog/Steps/ConversationalTemplate";
import EditTemplateDialog from "features/Influencer/Templates/TemplatesTable/EditTemplate";
import UnsavedChanges from "features/Influencer/Templates/TemplatesTable/EditTemplate/components/UnsavedChanges";
import { fetcherAuth } from "utils/api";
import { isPage } from "utils/general";
import { hasSocialMediaProfile } from "utils/influencer";
import { trackEvent } from "utils/tracking";
import { useBulkSend } from "utils/useBulkSend";

import PickTemplateMethodForm from "./Steps/PickTemplateMethodForm";
import ReviewBentoAiTemplate from "./Steps/ReviewBentoAiTemplate";
import SocialMediaLinksForm from "./Steps/SocialMediaLinksForm";
import CreateTemplateDialogWrapper from "./Wrapper";

const styles = {
  formContainer: {
    p: { xs: 3, sm: 5, md: 6 },
  },
  noOverflow: {
    overflowX: "hidden",
  },
  fullWidth: {
    width: "100%",
  },
};

interface Props {
  isDialog?: boolean;
}

const CreateTemplateDialog = ({ isDialog = true }: Props) => {
  const { setErrorAlert } = useContext(AlertContext);
  const { currentOrg } = useContext(OrganizationUserContext);
  const { getToken } = useAuth();
  const theme = useTheme();
  const isMobileScreen = useMediaQuery(theme.breakpoints.down("md"));
  const location = useLocation();
  const { profile, setProfile } = useContext(OrganizationUserContext);
  const {
    page,
    setPage,
    setCreateTemplateOnboardingDialogOpen,
    setProfileDialogOpen,
    setEmailBrandDialogOpen,
    incompleteProfile,
  } = useContext(OnboardingDialogsContext);
  const { fetchOutreachTemplates } = useContext(OutreachTemplatesContext);
  const { fetchOrganizationBulkSendSettings, fetchNiches } = useBulkSend();
  const [isDirty, setIsDirty] = useState<boolean>(false);
  const [openIsDirtyWarning, setOpenIsDirtyWarning] = useState<boolean>(false);

  const [onboardingForm, setOnboardingForm] = useState<Map>({});
  const [editTemplate, setEditTemplate] = useState<Template>({
    name: "",
    contentType: TemplateContentType.OUTREACH,
    subject: "",
    body: "",
  });
  const [followUpTemplate, setFollowUpTemplate] = useState<Template>({
    name: "",
    contentType: TemplateContentType.FOLLOWUP,
    subject: "",
    body: "",
  });
  const [loading, setLoading] = useState(false);

  const handleNext = async (updatedForm?: Map) => {
    if (page === Page.fromScratch && !hasSocialMediaProfile(profile)) {
      setPage(Page.socials);
    } else if (
      page === Page.fromScratch ||
      page === Page.socials ||
      page === Page.template
    ) {
      if (isPage(routes.home)) {
        setPage(Page.complete);
      } else {
        setProfile((prev) => ({
          ...prev,
          reviewedFirstTemplate: true,
        }));
        setCreateTemplateOnboardingDialogOpen(false);
        await fetchOutreachTemplates();
        if (isPage(routes.onboardingVaSelfServe)) {
          await fetchOrganizationBulkSendSettings();
          await fetchNiches();
        }
      }
      return;
    } else if (page === Page.bentoAi) {
      setEditTemplate(updatedForm?.outreach as Template);
      setFollowUpTemplate(updatedForm?.followup as Template);
      setPage(Page.template);
    }
  };

  const handleBack = async () => {
    trackEvent("Create Template Dialog Form - Back Button Pressed", {
      page,
      route: location.pathname,
    });
    if (page === Page.pitchType) {
      setCreateTemplateOnboardingDialogOpen(false);
      if (!isPage(routes.home)) {
        setProfileDialogOpen(true);
      }
    } else if (page === Page.fromScratch) {
      setPage(Page.pitchType);
    } else if (page === Page.bentoAi) {
      setPage(Page.pitchType);
    } else if (page === Page.template) {
      setPage(Page.bentoAi);
    } else if (page === Page.socials) {
      setPage(Page.fromScratch);
    }
  };

  const handleClose = () => {
    trackEvent("Create Template Dialog Form Closed", {
      page,
      route: location.pathname,
    });
    if (isDirty) {
      setOpenIsDirtyWarning(true);
      return;
    }
    confirmedClose();
  };

  const confirmedClose = () => {
    setPage(Page.pitchType);
    setCreateTemplateOnboardingDialogOpen(false);
  };

  const saveOrgProfile = async (onboardingForm: Map) => {
    setLoading(true);
    try {
      await fetcherAuth(
        getToken,
        `/api/organization/${currentOrg?.id}/onboarding`,
        "PUT",
        {},
        {
          ...onboardingForm,
          page: location.pathname,
        },
      );
      handleNext();
    } catch (error) {
      setErrorAlert(error);
    } finally {
      setLoading(false);
    }
  };

  return (
    <CreateTemplateDialogWrapper
      handleClose={handleClose}
      isMobileScreen={isMobileScreen}
      isDialog={isDialog}
    >
      <DialogContent sx={styles.noOverflow}>
        <Grid container sx={[styles.noOverflow, styles.fullWidth]}>
          <Grid sx={[styles.formContainer, styles.fullWidth]} item xs={12}>
            {page === Page.pitchType && (
              <PickTemplateMethodForm
                isDialog={isDialog}
                handleNext={(method?: TemplateMethod | null) => {
                  if (method === TemplateMethod.from_scratch) {
                    setPage(Page.fromScratch);
                  } else {
                    setPage(Page.bentoAi);
                  }
                }}
              />
            )}
            {page === Page.fromScratch && (
              <EditTemplateDialog
                propsHandleClose={handleBack}
                onSuccess={handleNext}
              />
            )}
            {page === Page.socials && (
              <SocialMediaLinksForm
                onboardingForm={onboardingForm}
                setOnboardingForm={setOnboardingForm}
                handleNext={async () => {
                  await saveOrgProfile(onboardingForm);
                }}
                loading={loading}
                handleBack={handleBack}
                handleSkip={handleNext}
              />
            )}
            {page === Page.bentoAi && (
              <ConversationalTemplate
                handleBack={handleBack}
                handleNext={handleNext}
              />
            )}
            {page === Page.template && (
              <ReviewBentoAiTemplate
                editTemplate={editTemplate}
                setEditTemplate={setEditTemplate}
                handleBack={handleBack}
                handleNext={handleNext}
                setIsDirty={setIsDirty}
                followUpTemplate={followUpTemplate}
                setFollowUpTemplate={setFollowUpTemplate}
              />
            )}

            {page === Page.complete && (
              <>
                <Typography variant="h4" paragraph sx={{ textAlign: "center" }}>
                  <Box
                    component="i"
                    color="primary.main"
                    sx={{ pr: 2 }}
                    className="fa-solid fa-party-horn"
                  />
                  Template Created!
                </Typography>
                <Typography paragraph sx={{ textAlign: "center" }}>
                  Congrats on creating your first template! This gives you a
                  ready-to-use message that makes reaching out to brands faster
                  and easier.
                </Typography>
                <Typography gutterBottom sx={{ textAlign: "center" }}>
                  Want create or edit your templates? You can further customize
                  your templates in the{" "}
                  <Link
                    component={RouterLink}
                    to={`/${routes.templates}`}
                    onClick={() => {
                      trackEvent(
                        "Create Template Dialog Form - Open Templates Page",
                        {
                          page,
                          route: location.pathname,
                        },
                      );
                      setCreateTemplateOnboardingDialogOpen(false);
                    }}
                    sx={{ textDecoration: "none" }}
                  >
                    <strong>template page</strong>
                  </Link>
                  .
                </Typography>
                {isPage(routes.home) && incompleteProfile && (
                  <>
                    <Divider sx={{ my: 3 }} />
                    <Typography
                      paragraph
                      variant="h4"
                      sx={{ textAlign: "center" }}
                    >
                      Next Step: Complete Your Profile
                    </Typography>
                    <Typography paragraph sx={{ textAlign: "center" }}>
                      To help you pitch brands effectively, fill in a few
                      details to get personalized brand recommendations tailored
                      to your content.
                    </Typography>
                    <Box display="flex" justifyContent="center">
                      <Button
                        disableElevation
                        sx={{ textAlign: "center" }}
                        variant="contained"
                        onClick={() => {
                          trackEvent(
                            "Create Template Dialog Form - Complete Profile Started",
                            {
                              page,
                              route: location.pathname,
                            },
                          );
                          setCreateTemplateOnboardingDialogOpen(false);
                          setProfileDialogOpen(true);
                        }}
                      >
                        Start
                      </Button>
                    </Box>
                  </>
                )}
                {isPage(routes.home) &&
                  !incompleteProfile &&
                  !profile?.hasOnboarded && (
                    <>
                      <Divider sx={{ my: 3 }} />
                      <Typography
                        paragraph
                        variant="h4"
                        sx={{ textAlign: "center" }}
                      >
                        Next Step: Send Your First Pitch
                      </Typography>
                      <Typography paragraph sx={{ textAlign: "center" }}>
                        To help you pitch brands effectively, let's guide you
                        through sending your first brand pitch using Bento.
                      </Typography>
                      <Box display="flex" justifyContent="center">
                        <Button
                          disableElevation
                          sx={{ textAlign: "center" }}
                          variant="contained"
                          onClick={() => {
                            trackEvent(
                              "Create Template Dialog Form - Send First Email Started",
                              {
                                page,
                                route: location.pathname,
                              },
                            );
                            setCreateTemplateOnboardingDialogOpen(false);
                            setEmailBrandDialogOpen(true);
                          }}
                        >
                          Start
                        </Button>
                      </Box>
                    </>
                  )}
              </>
            )}
          </Grid>
        </Grid>
      </DialogContent>
      <UnsavedChanges
        isUserFirstTemplate={true}
        confirmedClose={confirmedClose}
        setIsDirty={setIsDirty}
        setOpenIsDirtyWarning={setOpenIsDirtyWarning}
        openIsDirtyWarning={openIsDirtyWarning}
      />
    </CreateTemplateDialogWrapper>
  );
};

export default CreateTemplateDialog;
